import { Collapse, Divider, Group, Pagination, Paper, Select, Stack, Text, UnstyledButton } from "@mantine/core";
import { CaretDownIcon, CaretRightIcon } from "@phosphor-icons/react";
import qs from "qs";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import useSwr from "swr";
import { NetworkSettlementApi } from "../../apis";
import { InvoiceRow, InvoiceTable } from "../../components/invoice-table";
import { IGeneral } from "../../interfaces/IGeneral";
import { IReference } from "../../interfaces/IReference";
import { renderBusinessName } from "../../utils/render-business-name";
import { renderName } from "../../utils/render-name";

export const SalesInvoicesList = ({
  filters,
  title,
}: {
  title: string;
  filters: { businessId: string; type: string };
}) => {
  const [opened, setOpened] = useState(true);
  const [offset, setOffset] = useState({ page: 1, limit: 10 });
  const { overdueStatus } = useSelector((state: { general: IGeneral }) => state.general);

  const { data, isLoading } = useSwr(
    `inv.network_settlement.list.[${offset.page},${offset.limit}]?${qs.stringify(filters)}`,
    async () => await NetworkSettlementApi.invoice({ offset, filter: filters }),
  );

  const rows: InvoiceRow[] = useMemo(() => {
    const apiRows = data?.rows ?? [];
    return apiRows.map((r: any): InvoiceRow => {
      const ref = overdueStatus?.find((x: IReference) => x.code === r.overdueStatus);
      const status = ref
        ? { label: ref.name ?? r.overdueStatus ?? "-", color: ref.color ?? "gray" }
        : { label: r.overdueStatus ?? "-", color: "gray" };

      const employee = r.confirmedUser ? renderName(r.confirmedUser) : undefined;

      return {
        id: r.id ?? r.refCode,
        number: `#${r.refCode ?? "-"}`,
        customer: {
          name: renderBusinessName(r.receiverBusiness),
          email: r.receiverBusiness?.refCode ? `#${r.receiverBusiness.refCode}` : undefined,
        },
        totalAmount: Number(r.confirmedAmount ?? 0),
        paidAmount: Number(r.paidAmount ?? 0),
        remainingBalance: Number(r.amountToPay ?? 0),
        status,
        issueDate: filters.type === "CLOSED" ? r.paidDate : r.confirmedDate,
        dueDate: r.paymentDate,
        employee,
      };
    });
  }, [data, overdueStatus, filters.type]);

  const total = data?.count ?? 0;
  const pageCount = Math.ceil(total / offset.limit);

  return (
    <Paper withBorder px="xs">
      <UnstyledButton w="100%" onClick={() => setOpened(!opened)}>
        <Group>
          <Text component="h1">{title}</Text>
          {opened ? (
            <CaretDownIcon color="gray" size="1.2rem" />
          ) : (
            <CaretRightIcon color="gray" size="1.2rem" />
          )}
        </Group>
      </UnstyledButton>
      <Collapse expanded={opened}>
        <Divider mb="sm" />
        <Stack gap="sm" pb="sm">
          <InvoiceTable invoices={rows} loading={isLoading} />
          {pageCount > 1 && (
            <Group justify="flex-end" gap="sm">
              <Pagination
                total={pageCount}
                value={offset.page}
                onChange={(page: number) => setOffset((s) => ({ ...s, page }))}
                siblings={1}
                boundaries={1}
                withControls
                size="sm"
              />
              <Select
                value={`${offset.limit}`}
                onChange={(v: string | null) =>
                  v && setOffset({ page: 1, limit: parseInt(v, 10) })
                }
                w={90}
                size="xs"
                data={["10", "20", "50", "100"]}
              />
            </Group>
          )}
        </Stack>
      </Collapse>
    </Paper>
  );
};
